import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FaEdit, FaKey, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 1rem;
  }
`;

const SearchBar = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 300px;
  }
`;

const AddButton = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  white-space: nowrap;

  &:hover {
    background-color: #45a049;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  color: #555;
  font-size: 0.85rem;
  font-weight: 600;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.9rem;
  color: #333;
`;

const RoleBadge = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    switch (props.$role) {
      case 'admin': return '#e8eaf6';
      case 'user': return '#e3f2fd';
      default: return '#f3e5f5';
    }
  }};
  color: ${props => {
    switch (props.$role) {
      case 'admin': return '#283593';
      case 'user': return '#1565c0';
      default: return '#6a1b9a';
    }
  }};
`;

const ActiveBadge = styled.span`
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => props.$active ? '#e8f5e9' : '#ffebee'};
  color: ${props => props.$active ? '#2e7d32' : '#c62828'};
`;

const ActionButton = styled.button`
  padding: 0.3rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.2s;
  margin-right: 0.3rem;

  &:hover {
    background: #e0e0e0;
    color: #333;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 95%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h2`
  margin: 0 0 1.25rem 0;
  color: #333;
  font-size: 1.3rem;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FullWidth = styled.div`
  grid-column: span 2;

  @media (max-width: 768px) {
    grid-column: span 1;
  }
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.3rem;
  color: #555;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const Select = styled.select`
  padding: 0.6rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.95rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  padding: 0.6rem 1.25rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &.primary {
    background-color: #4CAF50;
    color: white;

    &:hover {
      background-color: #45a049;
    }
  }

  &.secondary {
    background-color: #f5f5f5;
    color: #333;

    &:hover {
      background-color: #e0e0e0;
    }
  }
`;

const InfoText = styled.div`
  text-align: center;
  padding: 0.5rem;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
`;

const Users = () => {
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUserId, setPasswordUserId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    company: '',
    phone: '',
    role: 'client'
  });

  const queryClient = useQueryClient();

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['users'],
    queryFn: userService.getAll,
  });

  const createMutation = useMutation({
    mutationFn: userService.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Usuario creado correctamente');
      closeModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Error al crear usuario');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => userService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Usuario actualizado');
      closeModal();
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Error al actualizar usuario');
    }
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id) => userService.toggleActive(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['users']);
      toast.success(data.isActive ? 'Usuario activado' : 'Usuario desactivado');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Error al cambiar estado');
    }
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, newPassword }) => userService.resetPassword(id, newPassword),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Contraseña actualizada');
      setShowPasswordModal(false);
      setPasswordUserId(null);
      setNewPassword('');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Error al resetear contraseña');
    }
  });

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      company: '',
      phone: '',
      role: 'client'
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    resetForm();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingUser) {
      const { password, ...data } = formData;
      updateMutation.mutate({ id: editingUser.id, data });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email || '',
      password: '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      company: user.company || '',
      phone: user.phone || '',
      role: user.role || 'client'
    });
    setShowModal(true);
  };

  const handleToggleActive = (user) => {
    const action = user.isActive ? 'desactivar' : 'activar';
    if (window.confirm(`¿${action.charAt(0).toUpperCase() + action.slice(1)} al usuario "${user.firstName} ${user.lastName}"?`)) {
      toggleActiveMutation.mutate(user.id);
    }
  };

  const handleResetPassword = (user) => {
    setPasswordUserId(user.id);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    resetPasswordMutation.mutate({ id: passwordUserId, newPassword });
  };

  const isSelf = (userId) => currentUser?.id === userId;

  const filteredUsers = users?.filter(user => {
    const search = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(search) ||
      user.lastName?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.company?.toLowerCase().includes(search)
    );
  }) || [];

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingState>Cargando usuarios...</LoadingState>
      </Container>
    );
  }

  if (error) {
    toast.error('Error al cargar los usuarios');
    return (
      <Container>
        <EmptyState>Error al cargar los usuarios</EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Usuarios</Title>
        <HeaderControls>
          <SearchBar
            type="text"
            placeholder="Buscar usuarios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AddButton onClick={() => { setEditingUser(null); resetForm(); setShowModal(true); }}>
            + Nuevo Usuario
          </AddButton>
        </HeaderControls>
      </Header>

      {filteredUsers.length === 0 ? (
        <EmptyState>
          {searchTerm ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
        </EmptyState>
      ) : (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Nombre</Th>
                  <Th>Email</Th>
                  <Th>Empresa</Th>
                  <Th>Rol</Th>
                  <Th>Activo</Th>
                  <Th>Último login</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <Td>{user.firstName} {user.lastName}</Td>
                    <Td>{user.email}</Td>
                    <Td>{user.company || '-'}</Td>
                    <Td><RoleBadge $role={user.role}>{user.role}</RoleBadge></Td>
                    <Td><ActiveBadge $active={user.isActive}>{user.isActive ? 'Activo' : 'Inactivo'}</ActiveBadge></Td>
                    <Td>{formatDate(user.lastLogin)}</Td>
                    <Td>
                      <ActionButton onClick={() => handleEdit(user)} title="Editar">
                        <FaEdit />
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleToggleActive(user)}
                        disabled={isSelf(user.id)}
                        title={user.isActive ? 'Desactivar' : 'Activar'}
                      >
                        {user.isActive ? <FaToggleOn /> : <FaToggleOff />}
                      </ActionButton>
                      <ActionButton onClick={() => handleResetPassword(user)} title="Resetear contraseña">
                        <FaKey />
                      </ActionButton>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
          <InfoText>{filteredUsers.length} usuario{filteredUsers.length !== 1 ? 's' : ''}</InfoText>
        </>
      )}

      {/* Create/Edit User Modal */}
      {showModal && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</ModalTitle>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Nombre *</Label>
                <Input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Apellido *</Label>
                <Input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </FormGroup>

              {!editingUser && (
                <FormGroup>
                  <Label>Contraseña *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    required
                    minLength={6}
                  />
                </FormGroup>
              )}

              <FormGroup>
                <Label>Empresa</Label>
                <Input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>Teléfono</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </FormGroup>

              <FormGroup>
                <Label>Rol</Label>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  disabled={editingUser && isSelf(editingUser.id)}
                >
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="client">Client</option>
                </Select>
              </FormGroup>

              <FullWidth>
                <ButtonGroup>
                  <Button type="button" className="secondary" onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="primary">
                    {editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
                  </Button>
                </ButtonGroup>
              </FullWidth>
            </Form>
          </ModalContent>
        </Modal>
      )}

      {/* Reset Password Modal */}
      {showPasswordModal && (
        <Modal onClick={() => { setShowPasswordModal(false); setPasswordUserId(null); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Resetear Contraseña</ModalTitle>
            <form onSubmit={handlePasswordSubmit}>
              <FormGroup>
                <Label>Nueva contraseña *</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  placeholder="Mínimo 6 caracteres"
                />
              </FormGroup>
              <ButtonGroup>
                <Button type="button" className="secondary" onClick={() => { setShowPasswordModal(false); setPasswordUserId(null); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="primary">
                  Cambiar Contraseña
                </Button>
              </ButtonGroup>
            </form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

export default Users;
